/**
 * @file Label plugin for projection grids.
 *
 */
import $ from 'jquery';
import _ from 'underscore';

import { createLabelSelectorView } from './create-label-selector-view';

/**
 * Projection grid labels plugin,
 * User need to set these things in config
 * filterService: the filterService which can get current filter using getCombinedFilter
 * labels: {
 *   i18n { getString }, localization setting
 *   i18nErrors { getODataErrors }, localization for odata errors
 *   LabelAssociationView:  the label association view, it's sub class of AssociationView
 *   router: { navigateToRoute }, specify the routing function
 *   dataContextManager: { getContext } provide function to get context of request
 *   cacheService: {cache} provide cache function
 *   instrumentation - instrumentation that already intilized
 *     and which should contains the senario of label and activity of createLabel
 *   jsdata
 *   s$label: schema for label
 * @param { object } definePlugin:  function pointer used to define plugin for grid
 * @returns {void}
 */
export const labelsPlugin = definePlugin => definePlugin('labels', [
  'config',
  'gridView',
  'toolbar',
  'columnChooser',
  'pagination',
  'crossPageSelectAll',
], (config, gridView, toolbar, columnChooser, pagination, crossPageSelectAll) => {
  const {
    i18n,
    i18nErrors,
    LabelAssociationView,
    router,
    dataContextManager,
    cacheService,
    instrumentation,
    jsdata,
    s$label,
    scope,
    edm,
    isForVNext = false,
    disableDeselectAll = false,
    isManagementRequest = undefined,
    perfMarker,
  } = _.result(config.plugins, 'labels', {});
  const appCfg = config.config || { get: () => ({}) };
  const { dataSource, filterService } = config;

  let labelSelectorView = null;
  let $dropdownContainer = null;
  let labelSelectorPerfMarker = null;
  const useSubMenu = true; // Add a flag to indicate the change with this flag useSubMenu should be related
  const labelDropdownId = 'dropdown-labels';
  const toolbarEditor = config.plugins.labels.toolbar ||
    toolbar.gridToolbarGroup.toolbarWithItem(labelDropdownId);

  function getEntityName() {
    if (!dataSource) {
      return undefined;
    }
    const entityName = dataSource.entity && dataSource.entity.name;
    const resourceName = dataSource.resource && dataSource.resource.name;
    return entityName || resourceName;
  }

  function getSelectionInfo(entityType) {
    const info = {
      entityType,
      scope,
    };

    if (crossPageSelectAll.isAllSelected) {
      info.allPages = true;
      info.selectedRowIds = null;
      info.selectedRowCount = gridView.totalCountRows;
    } else {
      info.allPages = false;
      info.selectedItems = gridView.selectedItems().map(x => _.pick(x, 'Id', 'Labels'));
      info.selectedRowIds = info.selectedItems.map(x => x.Id);
      info.selectedRowCount = info.selectedRowIds.length;
    }

    return info;
  }

  function createGridObjectForLegacyPlugins() {
    return {
      gridView,
      pgridPlugins: {
        pagination,
        filterService,
        crossPageSelectAll,
        columnChooser,
      },
    };
  }

  function showSelector(oldSelector, selectionInfo) {
    if (perfMarker) {
      labelSelectorPerfMarker = perfMarker.createChild('Label_Selector_View');
      labelSelectorPerfMarker.willInit();
    }

    const permissions = appCfg.get('Permissions');
    const isAccountLabelsEnabled =
      permissions && permissions.IsAccountLabelsEnabled;
    if (labelSelectorView) {
      labelSelectorView.remove();
    }

    labelSelectorView = createLabelSelectorView({
      grid: createGridObjectForLegacyPlugins(),
      selectionInfo,
      i18n,
      i18nErrors,
      router,
      LabelAssociationView,
      dataContextManager,
      cacheService,
      jsdata,
      s$label,
      instrumentation,
      isForVNext,
      toolbarEditor,
      toolbar,
      disableDeselectAll,
      edm,
      scope,
      customerLevelLabelEnabled: isAccountLabelsEnabled,
      isManagementRequest,
      gridName: config.name,
      perfMarker: labelSelectorPerfMarker,
      useSubMenu,
    });

    // useSubMenu, create a subMenu inside the dropdown with search bar and search results
    if (useSubMenu) {
      // change the role of the dropdown from menu to group
      $dropdownContainer.attr('role', 'group');
    }
    $dropdownContainer.append(labelSelectorView.el);

    labelSelectorView.render();
  }

  function unbindDropdownContainerClick() {
    if ($dropdownContainer && _.isFunction($dropdownContainer.off)) {
      $dropdownContainer.off('click');
    }
  }

  function setupLabelMenu() {
    unbindDropdownContainerClick();
    $dropdownContainer = toolbarEditor.$('#dropdown-labels');
    const entityName = getEntityName();

    $dropdownContainer.click(() => {
      const selectionInfo = getSelectionInfo(entityName);

      if (selectionInfo.selectedRowCount > 0) {
        showSelector(null, selectionInfo, gridView);
      }
    });
  }

  function updateToolbar() {
    const permissions = appCfg.get('Permissions');
    const isAccountLabelsEnabled =
      permissions && permissions.IsAccountLabelsEnabled;
    let shouldBeDisabled = !gridView || gridView.selectedItems().length === 0;
    if (isForVNext) {
      shouldBeDisabled = appCfg.get('Permissions').IsReadOnlyUser || (appCfg.get('CurrentAccountInActive') && scope.values.aid !== undefined);
    }

    if (isAccountLabelsEnabled) {
      shouldBeDisabled = shouldBeDisabled ||
        (!crossPageSelectAll.isAllSelected && gridView.selectedItems() && gridView.selectedItems()
          .some(item => item.AccountType === 'ManagerAccount'));
    }

    const updateToolbarConfig = {
      type: 'dropdown',
      id: labelDropdownId,
      classes: ['btn-secondary'],
      button: {
        text: i18n.getString('Grid_Labels'),
        disabled: shouldBeDisabled,
      },
    };

    if (isForVNext) {
      updateToolbarConfig.iconLeftGlyphName = 'ba-Label';

      updateToolbarConfig.onClick = (event) => {
        // click inside label selector, just return
        if ($(event.target).closest('.labels-selector').length > 0) {
          return;
        }
        // put labels inside the ul
        $dropdownContainer = $('#dropdown-labels').parent().find('ul.dropdown-menu');
        const selectionInfo = getSelectionInfo(getEntityName());

        if (selectionInfo.selectedRowCount > 0) {
          showSelector(null, selectionInfo, gridView);
        }
      };
    }
    toolbarEditor.update(updateToolbarConfig);
  }

  updateToolbar();

  // Don't need to redraw toolbar for vnext
  if (!isForVNext) {
    if (gridView) {
      gridView.on('didUpdate', () => {
        updateToolbar();
        setupLabelMenu();
      });
    }
  }

  function removeView() {
    unbindDropdownContainerClick();

    if (labelSelectorView) {
      labelSelectorView.remove();
    }
  }


  const permissions = appCfg.get('Permissions');
  const isAccountLabelsEnabled =
    permissions && permissions.IsAccountLabelsEnabled;

  if (isAccountLabelsEnabled) {
    gridView.on('didSelect', () => {
      updateToolbar();
    });
  }
  return {
    remove: removeView,
  };
});
